/**
 * Personnel List Page - Sprint 6 + Sprint 11A Layout Migration
 * Avida branding: white/gray base, blue accents, yellow highlights for badges/alerts
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { personnelAPI, usersAPI } from "../lib/api";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
} from "../components/ui/card";
import {
  Users,
  Plus,
  RefreshCw,
  Search,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Grid3X3,
} from "lucide-react";

const STATUS_COLORS = {
  Active: "bg-green-100 text-green-800 border-green-200",
  OnLeave: "bg-yellow-100 text-yellow-800 border-yellow-200",
  Terminated: "bg-gray-100 text-gray-600 border-gray-200",
  Archived: "bg-red-100 text-red-700 border-red-200",
};

const TYPE_LABELS = {
  Caregiver: "Caregiver",
  RN: "RN",
  OfficeStaff: "Office Staff",
  Contractor: "Contractor",
};

const PersonnelListPage = () => {
  const navigate = useNavigate();
  const { user, logout, canManagePersonnel } = useAuth();
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    user_id: "",
    personnel_type: "Caregiver",
    hire_date: "",
    first_name: "",
    last_name: "",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== "all") params.personnel_type = typeFilter;
      if (statusFilter !== "all") params.status_filter = statusFilter;
      
      // Only fetch users list if user can manage personnel (for Create modal)
      const promises = [personnelAPI.list(params)];
      if (canManagePersonnel) {
        promises.push(usersAPI.list({ page_size: 200 }));
      }
      
      const results = await Promise.all(promises);
      setPersonnel(results[0].data.personnel || []);
      
      if (canManagePersonnel && results[1]) {
        setUsers((results[1].data.users || []).filter(u => !u.is_archived));
      }
    } catch (error) {
      toast.error("Failed to load personnel");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter, canManagePersonnel]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...createForm };
      if (!payload.user_id) delete payload.user_id;
      if (!payload.first_name) delete payload.first_name;
      if (!payload.last_name) delete payload.last_name;
      if (!payload.hire_date) delete payload.hire_date;
      
      await personnelAPI.create(payload);
      toast.success("Personnel record created");
      setShowCreateModal(false);
      setCreateForm({
        user_id: "",
        personnel_type: "Caregiver",
        hire_date: "",
        first_name: "",
        last_name: "",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create personnel");
    }
  };

  // Filter by search term (name/email)
  const filteredPersonnel = personnel.filter(p => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const name = (p.full_name || "").toLowerCase();
    const email = (p.user_email || "").toLowerCase();
    return name.includes(search) || email.includes(search);
  });

  // Calculate compliance stats
  const stats = {
    total: filteredPersonnel.length,
    compliant: filteredPersonnel.filter(p => p.is_compliant === true).length,
    nonCompliant: filteredPersonnel.filter(p => p.is_compliant === false).length,
    active: filteredPersonnel.filter(p => p.status === "Active").length,
  };

  return (
    <Layout title="Personnel & Training">
      <div className="space-y-6" data-testid="personnel-list-page">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-slate-700">{stats.total}</div>
              <div className="text-sm text-slate-500">Total Personnel</div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-avida-blue">{stats.active}</div>
              <div className="text-sm text-slate-500">Active</div>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
                <div className="text-2xl font-bold text-emerald-700">{stats.compliant}</div>
              </div>
              <div className="text-sm text-emerald-600">Compliant</div>
            </CardContent>
          </Card>
          <Card className={stats.nonCompliant > 0 ? "bg-avida-yellow-50 border-avida-yellow-300" : "bg-white border-slate-200"}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                {stats.nonCompliant > 0 && <AlertTriangle className="h-5 w-5 text-avida-yellow-600" />}
                <div className={`text-2xl font-bold ${stats.nonCompliant > 0 ? "text-avida-yellow-700" : "text-slate-700"}`}>
                  {stats.nonCompliant}
                </div>
              </div>
              <div className={`text-sm ${stats.nonCompliant > 0 ? "text-yellow-600" : "text-gray-500"}`}>Non-Compliant</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Actions */}
        <Card className="bg-white border-gray-200 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-xs">
                <Search className="h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-9"
                  data-testid="search-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-32 h-9" data-testid="status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="OnLeave">On Leave</SelectItem>
                    <SelectItem value="Terminated">Terminated</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">Type</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-36 h-9" data-testid="type-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="Caregiver">Caregiver</SelectItem>
                    <SelectItem value="RN">RN</SelectItem>
                    <SelectItem value="OfficeStaff">Office Staff</SelectItem>
                    <SelectItem value="Contractor">Contractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button variant="outline" size="sm" onClick={fetchData} className="h-9">
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>

              <Button variant="outline" size="sm" onClick={() => navigate("/training-matrix")} className="h-9">
                <Grid3X3 className="h-4 w-4 mr-1" /> Training Matrix
              </Button>

              <div className="ml-auto">
                {canManagePersonnel && (
                  <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="create-personnel-btn">
                        <Plus className="h-4 w-4 mr-1" /> Add Personnel
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Personnel Record</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreate} className="space-y-4">
                        <div>
                          <Label>Personnel Type *</Label>
                          <Select
                            value={createForm.personnel_type}
                            onValueChange={(v) => setCreateForm({ ...createForm, personnel_type: v })}
                          >
                            <SelectTrigger data-testid="create-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                                <SelectItem key={k} value={k}>{v}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Link to User Account (Optional)</Label>
                          <Select
                            value={createForm.user_id || "none"}
                            onValueChange={(v) => setCreateForm({ ...createForm, user_id: v === "none" ? "" : v })}
                          >
                            <SelectTrigger data-testid="create-user-select">
                              <SelectValue placeholder="Select user or leave empty" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None (Manual entry)</SelectItem>
                              {users.map((u) => (
                                <SelectItem key={u.id} value={u.id}>
                                  {u.first_name} {u.last_name} ({u.email}) - {u.role}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {!createForm.user_id && (
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>First Name</Label>
                              <Input
                                value={createForm.first_name}
                                onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
                                placeholder="First name"
                                data-testid="create-first-name"
                              />
                            </div>
                            <div>
                              <Label>Last Name</Label>
                              <Input
                                value={createForm.last_name}
                                onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
                                placeholder="Last name"
                                data-testid="create-last-name"
                              />
                            </div>
                          </div>
                        )}

                        <div>
                          <Label>Hire Date</Label>
                          <Input
                            type="date"
                            value={createForm.hire_date}
                            onChange={(e) => setCreateForm({ ...createForm, hire_date: e.target.value })}
                            data-testid="create-hire-date"
                          />
                        </div>

                        <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="create-submit-btn">
                          Create Personnel
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Personnel Table */}
        <Card className="bg-white border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">
              Personnel
              <span className="text-sm font-normal text-gray-500 ml-2">
                {filteredPersonnel.length} total
              </span>
            </h3>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
              Loading...
            </div>
          ) : filteredPersonnel.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              No personnel found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-gray-700">Name</TableHead>
                  <TableHead className="font-semibold text-gray-700">Type</TableHead>
                  <TableHead className="font-semibold text-gray-700">Status</TableHead>
                  <TableHead className="font-semibold text-gray-700">Hire Date</TableHead>
                  <TableHead className="font-semibold text-gray-700">Compliance</TableHead>
                  <TableHead className="font-semibold text-gray-700">Issues</TableHead>
                  <TableHead className="font-semibold text-gray-700">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPersonnel.map((p) => (
                  <TableRow
                    key={p.id}
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => navigate(`/personnel/${p.id}`)}
                    data-testid={`personnel-row-${p.id}`}
                  >
                    <TableCell>
                      <div>
                        <div className="font-medium text-gray-900">{p.full_name || "N/A"}</div>
                        {p.user_email && (
                          <div className="text-sm text-gray-500">{p.user_email}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-700">{TYPE_LABELS[p.personnel_type] || p.personnel_type}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[p.status]} variant="outline">{p.status}</Badge>
                    </TableCell>
                    <TableCell className="text-gray-600">{p.hire_date || "-"}</TableCell>
                    <TableCell>
                      {p.is_compliant === true ? (
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm">Compliant</span>
                        </div>
                      ) : p.is_compliant === false ? (
                        <div className="flex items-center gap-1 text-yellow-600">
                          <AlertTriangle className="h-4 w-4" />
                          <span className="text-sm">Issues</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {p.compliance_issues && p.compliance_issues.length > 0 ? (
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                          {p.compliance_issues.length} issue{p.compliance_issues.length > 1 ? 's' : ''}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/personnel/${p.id}`);
                        }}
                        data-testid={`view-personnel-${p.id}`}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default PersonnelListPage;
